/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SoundToggleButton from "../components/common/SoundToggleButton";

function HomePage() {
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const navigate = useNavigate();

  const videoUrl = "/assets/movies/home-bg-video.mp4";

  const togglePanel = () => {
    setIsPanelExpanded(!isPanelExpanded);
  };

  const goToNextSection = () => {
    setCurrentSection((prev) => (prev + 1) % 9);
  };

  const goToPrevSection = () => {
    setCurrentSection((prev) => (prev - 1 + 9) % 9);
  };

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onMouseDown = (e) => {
    setIsDragging(true);
    setDragEnd(null);
    setDragStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setDragEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd) {
      setIsDragging(false);
      return;
    }

    const distance = dragStart - dragEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNextSection();
    } else if (isRightSwipe) {
      goToPrevSection();
    }

    setIsDragging(false);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNextSection();
    } else if (isRightSwipe) {
      goToPrevSection();
    }
  };
  // Add this useEffect to handle global drag state
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }

    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging]);

  const sections = [
    // Section 0: Hero Section
    <div className="relative h-screen overflow-hidden">
      {/* Background Video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex items-center justify-start">
        <div
          className="w-full max-w-none text-white animate-fade-in-left
      px-10 md:px-12 md:w-10/12 md:max-w-2xl
      lg:px-16 lg:w-8/12 lg:max-w-3xl
      xl:px-24 xl:w-7/12 xl:max-w-4xl
      2xl:px-32 2xl:w-6/12 2xl:max-w-5xl
      4xl:px-48 4xl:w-5/12 4xl:max-w-6xl"
        >
          <h1 className="leading-tight mb-2 text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl 4xl:text-9xl">MODERN</h1>
          <h1 className="leading-tight mb-2 text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl 4xl:text-9xl">LIVING</h1>
          <h1 className="leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl 2xl:mb-8 4xl:text-9xl 4xl:mb-12">REFINED</h1>

          <p className="text-gray-200 mb-8 text-sm leading-relaxed md:text-base lg:text-lg lg:leading-relaxed xl:text-xl xl:leading-relaxed 2xl:text-2xl 2xl:leading-relaxed 2xl:mb-12 4xl:text-3xl 4xl:leading-relaxed 4xl:mb-16">
            This residence is your everyday gateway - a serene escapade amid
            Dubai's vibrant energy. This iconic residence offers modern living,
            top tier amenities and smart AI integration at its finest in the most
            centralized location of UAE.
          </p>
        </div>
      </div>

      {/* Attribution - bottom right corner */}
      <div className="absolute bottom-10 right-2 z-10 text-xs text-white/10 bg-black bg-opacity-10 px-2 py-1 rounded">
        Cityscape Stock Videos by <a href="https://www.vecteezy.com/free-videos/cityscape" target="_blank" rel="noopener noreferrer" className="underline">Vecteezy</a>
      </div>
    </div>
    ,
    // Section 1: Discover Laguna
    <div className="relative min-h-screen flex flex-col lg:flex-row items-center justify-start overflow-hidden px-8 sm:px-6 md:px-12 lg:px-24 xl:px-32 2xl:px-48 py-8 lg:py-0">
      {/* Background Image Layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50 z-0 backdrop-blur-lg" />

      {/* Left: Image */}
      <div className="w-full lg:w-5/12 mt-20 lg:mt-20 mb-6 lg:mb-0 z-20 flex justify-center lg:justify-start">
        <img
          src="/assets/images/building.png"
          alt="Feature"
          className="object-contain w-[200px] sm:w-[200px] md:w-[250px] lg:w-[300px] max-w-full max-h-[60vh]"
        />
      </div>

      {/* Right: Text */}
      <div className="w-full lg:w-7/12 text-[#DCC5B7] animate-fade-in-right z-20 backdrop-blur-sm px-2 sm:px-0">
        <h1 className="mb-1 leading-tight text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl">
          DISCOVER
        </h1>
        <h1 className="mb-1 leading-tight text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl">
          YOUR
        </h1>
        <h1 className="mb-1 leading-tight text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl">
          DREAM
        </h1>
        <h1 className="mb-4 md:mb-6 lg:mb-8 leading-tight text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl">
          RESIDENCIES
        </h1>

        <h2 className="font-bold mb-3 leading-tight text-white sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl">
          BY BnW
        </h2>

        <p className="text-xs sm:text-sm lg:text-base xl:text-lg 2xl:text-xl text-white leading-relaxed">
          Located in Dubai’s City of Arabia and overlooking one of the UAE’s
          most iconic views, This Residence offers modern studios, 1, 2, and
          3-bedroom apartments, perfect for both individuals and families.
        </p>

        <p className="pt-3 text-xs sm:text-sm lg:text-base xl:text-lg 2xl:text-xl text-white leading-relaxed">
          Enjoy panoramic views, smart home automation, and over 30 luxury
          amenities including VR golf, cinema rooms, yoga spaces, and infinity
          pools. This Residence seamlessly blends resort-style living with
          effortless access to Dubai’s key business and leisure hubs.
        </p>
      </div>
    </div>,
    // Section 2: Location
    <div
      className="relative w-full min-h-screen text-white overflow-hidden 
    py-8 md:py-12 lg:py-16 px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 2xl:px-32 4xl:px-48"
    >
      {/* Background Image Layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50 z-0 backdrop-blur-lg" />

      {/* Giant LOCATION Text (hidden on mobile) */}
      <h1
        className=" sm:block absolute bottom-16 lg:bottom-12 z-0 leading-none pointer-events-none select-none text-[#DCC5B7]
      left-4 sm:left-6 md:left-12 lg:left-16 xl:left-24 2xl:left-32 4xl:left-48
      text-[4rem] md:text-[5rem] lg:text-[5rem] xl:text-[6rem] 2xl:text-[8rem] 4xl:text-[12rem]"
      >
        LOCATION
      </h1>

      {/* Main Content */}
      <div className="relative mt-20 px-6 z-20 flex flex-col lg:flex-row gap-6 h-full">
        {/* Image (hidden on small screens) */}
        <div className="hidden lg:flex w-full lg:w-1/3 items-center justify-center">
          <img
            src="/assets/images/image-3.png"
            alt="Location Visual"
            className="max-h-[50vh] xl:max-h-[60vh] object-contain"
          />
        </div>

        {/* Map (goes to top on mobile) */}
        <div className="w-full lg:w-1/3 px-0 lg:px-6 flex items-center">
          <iframe
            title="Google Map - Dubai"
            className="w-full rounded-xl h-[30vh] sm:h-[40vh] md:h-[45vh] lg:h-[50vh] xl:h-[60vh]"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d115889.38203524164!2d55.18924171089928!3d25.0763810438714!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5c969cd6e2d7%3A0x1ee7b8b23d2ef5d1!2sDubai!5e0!3m2!1sen!2slk!4v1718625177062!5m2!1sen!2slk"
          />
        </div>

        {/* Text Section */}
        <div className="w-full lg:w-1/3 flex flex-col justify-center px-0 lg:px-6 pt-6 lg:pt-0">
          <div className="mb-6 lg:mb-4">
            <p className="leading-relaxed text-[#DCC5B7] text-sm md:text-base lg:text-md xl:text-xl 2xl:text-2xl 4xl:text-3xl">
              Positioned in one of the most sought-after neighborhoods, This
              Residence ensures effortless access to essential destinations,
              seamlessly combining comfort and connectivity.
            </p>
          </div>

          <div className="text-[#DCC5B7] pt-4 md:pt-6 lg:pt-10">
            <p className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl 4xl:text-7xl">
              LOCATION
            </p>
            <p className="mb-4 text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl 4xl:text-7xl">
              HIGHLIGHTS:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-xs md:text-sm lg:text-base xl:text-lg 2xl:text-xl 4xl:text-2xl">
              <li>18 Minutes to Dubai Outlet Mall</li>
              <li>20 Minutes to Dubai International Airport</li>
              <li>20 Minutes to Downtown Dubai and Dubai Mall</li>
              <li>30 Minutes to Al Maktoum International Airport</li>
            </ul>
          </div>
        </div>
      </div>
    </div>,

    // Section 3: Explore
    <div className="relative w-full h-screen overflow-hidden">
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src="https://firebasestorage.googleapis.com/v0/b/imldemo.firebasestorage.app/o/demo%2Fmovie_taj.mp4?alt=media&token=c62ce748-e4ac-4c3f-a775-90274522c4c3 "
        autoPlay
        loop
        muted
        playsInline
        controls
      />
      {/* Optional: Dark overlay for readability */}
      <div className="absolute inset-0  z-10 pointer-events-none"></div>
    </div>,
    // Section 4: Interior
    <div className="relative min-h-screen text-white flex flex-col lg:flex-row items-center justify-start overflow-hidden px-8 sm:px-6 md:px-12 lg:px-24 xl:px-32 2xl:px-48 py-8 lg:py-0">
      {/* Background Image Layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50 z-0 backdrop-blur-lg" />

      {/* INTERIOR Text */}
      <h1
        className="absolute bottom-16 lg:bottom-12 z-0 pointer-events-none leading-none select-none text-[#DCC5B7]
        left-6 md:left-12 lg:left-16 xl:left-24 2xl:left-32 4xl:left-48
        text-[4rem] md:text-[5rem] lg:text-[6rem] xl:text-[8rem] 2xl:text-[10rem] 4xl:text-[12rem]"
      >
        INTERIOR
      </h1>

      {/* Main Content */}
      <div className="relative z-20 flex flex-col lg:flex-row gap-8 md:gap-12 lg:gap-16 h-full mt-20 px-6 lg:mt-0 lg:px-0">
        {/* Left: Text Content */}
        <div className="w-full lg:w-1/3 flex flex-col justify-center">
          <h1 className="mb-2 text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 4xl:text-8xl">
            AESTHETICS
          </h1>
          <h1 className="mb-2 text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 4xl:text-8xl">
            COMPLEMENT
          </h1>
          <h1 className="mb-6 text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 4xl:text-8xl">
            COMFORT
          </h1>
          <p className="leading-relaxed text-white text-sm md:text-base lg:text-md xl:text-lg 2xl:text-xl 4xl:text-3xl">
            This Residence offers a wide range of apartment types in various
            sizes and layouts. Studio and 1-bedroom units start from 377–484 sq.
            ft. at AED 850K–950K, while larger 1-beds go up to 1,205 sq. ft.,
            priced up to AED 1.8M. Options like 1-bedroom lofts and units with
            maid's rooms start at AED 1.8M.
          </p>
        </div>

        {/* Right: Floating Images */}
        <div className="w-full lg:w-2/3 mt-20 relative flex items-center justify-center">
          <img
            src="/assets/images/image-4.png"
            alt="Center Image"
            className="rounded-2xl shadow-xl z-10 object-cover
        w-40 h-40 md:w-56 md:h-56 lg:w-64 lg:h-64 xl:w-64 xl:h-64 2xl:w-96 2xl:h-96"
          />
          <img
            src="/assets/images/image-5.png"
            alt="Top Left Image"
            className="rounded-2xl shadow-xl absolute left-[50%] top-[50%] transform -translate-x-full -translate-y-full z-20 object-cover
        w-40 h-40 md:w-56 md:h-56 lg:w-64 lg:h-64 xl:w-64 xl:h-64 2xl:w-96 2xl:h-96"
          />
          <img
            src="/assets/images/image-6.png"
            alt="Bottom Right Image"
            className="rounded-2xl shadow-lg absolute right-[50%] bottom-[50%] transform translate-x-full translate-y-full z-0 object-cover
        w-40 h-40 md:w-56 md:h-56 lg:w-64 lg:h-64 xl:w-64 xl:h-64 2xl:w-96 2xl:h-96"
          />
        </div>
      </div>
    </div>,

    // Section 5: Exterior
    <div
      className="relative w-full h-screen overflow-hidden py-8 md:py-12 lg:py-16 text-white
      px-6 md:px-12 lg:px-16 xl:px-24 2xl:px-32 4xl:px-48"
    >
      {/* Background Image */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50 z-0 backdrop-blur-lg" />

      {/* Main Content */}
      <div className="relative z-10 flex flex-col lg:flex-row gap-8 md:gap-12 lg:gap-16 h-full mt-20 px-6 lg:mt-0 lg:px-0">
        {/* Left: Text Section */}
        <div className="w-full lg:w-1/3 flex flex-col justify-center pr-0">
          <div className="flex flex-col">
            <h1 className="mb-2 text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 4xl:text-8xl">
              MODERN
            </h1>
            <h1 className="mb-2 text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 4xl:text-8xl">
              ELEGANCE
            </h1>
            <h1 className="mb-2 text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 4xl:text-8xl">
              INSPIRED BY
            </h1>
            <h1 className="mb-6 text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 4xl:text-8xl">
              NATURE
            </h1>
          </div>
          <div>
            <p
              className="leading-relaxed text-white/80
              text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl 4xl:text-3xl"
            >
              The Residence features a designer-inspired architecture that
              reflects a future first design. An outstanding exterior and curved
              lines give the buildings a contemporary look while allowing plenty
              of natural light and stunning views of Dubai's skyline. Built with
              eco-friendly materials and smart technology, the design combines
              style, sustainability, and comfort.
            </p>
          </div>
        </div>

        {/* Right: Image + Text Below */}
        <div className="w-full lg:w-2/3 relative mt-20 flex flex-col items-end justify-center">
          <img
            src="/assets/images/Image-exterior.png"
            alt="Center Image"
            className="w-full max-h-[50vh] md:max-h-[60vh] lg:max-h-[70vh] xl:max-h-[60vh] object-cover rounded-xl z-10"
          />
          <h1
            className="mt-4 md:mt-6 lg:mt-8 text-[#DCC5B7] tracking-widest whitespace-nowrap select-none
            text-[3rem] md:text-[4rem] lg:text-[6rem] xl:text-[8rem] 2xl:text-[10rem] 4xl:text-[12rem]"
          >
            EXTERIOR
          </h1>
        </div>
      </div>
    </div>,

    // Section 6: Territory
    <div
      className="relative w-full h-screen overflow-hidden py-8 md:py-12 lg:py-16 text-white
      px-6 md:px-12 lg:px-16 xl:px-24 2xl:px-32 4xl:px-48"
    >
      {/* Background Image Layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50 z-0 backdrop-blur-lg" />

      {/* Main Content */}
      <div className="relative z-10 flex flex-col lg:flex-row gap-8 md:gap-12 lg:gap-16 h-full mt-20 px-6 lg:mt-0 lg:px-0">
        {/* Left: Image */}
        <div className="w-full lg:w-2/3 relative flex items-center justify-start">
          <img
            src="/assets/images/territory.png"
            alt="Center Image"
            className="w-full max-h-[50vh] md:max-h-[60vh] lg:max-h-[70vh] xl:max-h-[80vh] object-cover rounded-xl z-10"
          />
        </div>

        {/* Right: Text Content */}
        <div className="w-full lg:w-1/3 text-white flex flex-col justify-center pr-0 lg:pr-8">
          <div className="flex flex-col">
            <h1 className="mb-2 text-3xl md:text-4xl lg:text-4xl xl:text-4xl 2xl:text-5xl 4xl:text-8xl">
              CONTRAST
            </h1>
            <h1 className="mb-2 text-3xl md:text-4xl lg:text-4xl xl:text-4xl 2xl:text-5xl 4xl:text-8xl">
              OF ARCHITECTURE
            </h1>
            <h1 className="mb-6 text-3xl md:text-4xl lg:text-4xl xl:text-4xl 2xl:text-5xl 4xl:text-8xl">
              AND NATURE
            </h1>
          </div>

          <div className="flex z-20">
            <h1
              className="mb-4 text-[#DCC5B7] leading-none
              text-[3rem] md:text-[4rem] lg:text-[6rem] xl:text-[6rem] 2xl:text-[8rem] 4xl:text-[12rem]"
            >
              TERRITORY
            </h1>
          </div>

          <div>
            <p
              className="text-white/80
              text-sm md:text-base lg:text-md xl:text-md 2xl:text-xl 4xl:text-3xl"
            >
              Living at this Residence brings a resort-inspired lifestyle to the
              heart of Dubai. With uninterrupted views of the Dubai skyline,
              residents experience the tranquility of luxury living alongside
              high-end amenities. The community's blend of nature, wellness, and
              urban energy offers an elevated lifestyle that balances relaxation
              with activity, making every day feel like a vacation.
            </p>
          </div>
        </div>
      </div>
    </div>,
    // Section 7: Amenities
    <div
      className="relative h-screen bg-cover bg-center flex flex-col justify-between py-8 md:py-12 lg:py-16 overflow-hidden
    px-6 md:px-12 lg:px-16 xl:px-24 2xl:px-32 4xl:px-48"
      style={{
        backgroundImage: `url('/assets/images/yoga.jpg')`,
      }}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/50 z-0" />

      {/* Title at top-left */}
      <div className="relative z-10 flex justify-start items-start mt-20 md:mt-12 lg:mt-20">
        <h1
          className="mb-4 text-[#DCC5B7] leading-none
        text-[3rem] md:text-[4rem] lg:text-[6rem] xl:text-[8rem] 2xl:text-[9rem] 4xl:text-[12rem]"
        >
          AMENITIES
        </h1>
      </div>

      {/* Amenities Grid at the bottom */}
      <div className="relative z-10 w-full flex flex-wrap justify-center md:justify-between gap-2 md:gap-4">
        {[
          { icon: "/assets/icons/beach.png", text: "Sun Bed and Lounge Areas" },
          { icon: "/assets/icons/tree-02.png", text: "Green Gardens" },
          {
            icon: "/assets/icons/yoga-02.png",
            text: "Yoga and meditation spaces",
          },
          { icon: "/assets/icons/pool.png", text: "Lagoon and Infinity pool" },
          { icon: "/assets/icons/beach.png", text: "Sun Bed and Lounge Areas" },
          { icon: "/assets/icons/tree-02.png", text: "Green Gardens" },
          {
            icon: "/assets/icons/yoga-02.png",
            text: "Yoga and meditation spaces",
          },
          { icon: "/assets/icons/pool.png", text: "Lagoon and Infinity pool" },
        ].map((item, index) => (
          <div
            key={index}
            className="flex-1 flex flex-col items-center text-white bg-black/10 backdrop-blur-md border border-white/20 rounded-xl break-words
          py-3 px-2 md:py-4 md:px-3 lg:py-6 lg:px-4
          min-w-[80px] max-w-[120px] md:min-w-[100px] md:max-w-[140px] lg:min-w-[120px] lg:max-w-[160px]"
          >
            <img
              src={item.icon}
              alt={item.text}
              className="mb-2 md:mb-3 lg:mb-4 
            h-8 w-8 md:h-12 md:w-12 lg:h-16 lg:w-16"
            />
            <p
              className="font-medium text-center leading-snug mt-1 md:mt-2
            text-xs md:text-sm lg:text-base"
            >
              {item.text}
            </p>
          </div>
        ))}
      </div>
    </div>,
    <div
      className="relative w-full h-screen overflow-hidden text-[#DCC5B7]
    px-6 md:px-12 lg:px-16 xl:px-24 2xl:px-32 4xl:px-48 py-8 md:py-12 lg:py-16"
    >
      enqire now section
      {/* Background Image Layer */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/images/home-bg.png')",
          opacity: 1,
          filter: "blur(30px)",
        }}
      />

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/40 z-0" />

      {/* Foreground Content */}
      <div className="relative z-10 h-full flex flex-col lg:flex-row items-center justify-center gap-12 md:gap-16 lg:gap-24">
        {/* Left Column: Heading */}
        <div className="w-full h-full lg:w-1/2 flex flex-col justify-end items-start text-left">
          <h1
            className="mb-6 leading-none 
        text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl 4xl:text-[10rem]"
          >
            ENQUIRE
          </h1>
          <h1
            className="-mt-6 leading-none 
        text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl 4xl:text-[10rem]"
          >
            NOW
          </h1>
        </div>

        {/* Right Column: Form */}
        <div className="w-full lg:w-1/2 flex justify-center items-center">
          <form
            className="w-full max-w-md bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 md:p-8 space-y-6 shadow-lg"
            onSubmit={(e) => {
              e.preventDefault();
              setDialogVisible(true);
              setShowDialog(true);
              setTimeout(() => {
                setShowDialog(false);
                setTimeout(() => setDialogVisible(false), 300); // wait for fade-out to complete
              }, 10000);
            }}
          >
            {/* Logo */}
            <div className="w-full flex justify-center mb-2">
              <img
                src="/assets/images/bnw-logo.png"
                alt="Logo"
                className="h-20 md:h-24 lg:h-28 object-contain"
              />
            </div>

            <div>
              <label className="block text-sm md:text-base mb-1 text-[#DCC5B7]">
                Email
              </label>
              <input
                required
                type="email"
                placeholder="your@email.com"
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#DCC5B7]"
              />
            </div>

            <div>
              <label className="block text-sm md:text-base mb-1 text-[#DCC5B7]">
                Contact Number
              </label>
              <input
                required
                type="tel"
                placeholder="+971-XXXX-XXXX"
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#DCC5B7]"
              />
            </div>

            <div>
              <label className="block text-sm md:text-base mb-1 text-[#DCC5B7]">
                Your Message
              </label>
              <textarea
                required
                rows={4}
                placeholder="Type your message..."
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#DCC5B7]"
              />
            </div>

            <button
              type="submit"
              className="w-full py-3 text-white font-medium bg-[#DCC5B7]/30 hover:bg-[#DCC5B7]/50 border border-amber-500/30 rounded-full transition-all duration-300"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    </div>,

    // <div className="relative w-full h-screen overflow-hidden px-6 lg:px-48 py-16 text-[#DCC5B7]">
    //   {/* Background Image Layer */}
    //   <div
    //     className="absolute inset-0 z-0 bg-cover bg-center"
    //     style={{
    //       backgroundImage: "url('/assets/images/home-bg.png')",
    //       opacity: 1,
    //       filter: "blur(30px)",
    //     }}
    //   />

    //   {/* Dark Overlay */}
    //   <div className="absolute inset-0 bg-black/40 z-0" />

    //   {/* Foreground Content */}
    //   <div className="relative z-10 h-full flex flex-col justify-center">
    //     <h1 className="text-6xl">38RG+88 - CITY OF </h1>
    //     <h1 className="text-6xl mt-4">ARABIA - DUBAI</h1>
    //     <h1 className="text-[8rem] mt-48">800 663 338</h1>

    //     <div className="flex justify-between mt-8">
    //       <div className="flex flex-col items-start">
    //         <h1 className="text-xl">Sales office</h1>
    //         <h1 className="text-xl">every day from 09:00 – 21:00</h1>
    //       </div>
    //       <div className="flex">
    //         <a
    //           href="https://facebook.com"
    //           target="_blank"
    //           rel="noopener noreferrer"
    //         >
    //           <img
    //             src="/assets/icons/facebook-02.png"
    //             className="h-8 w-8 mx-8"
    //           />
    //         </a>
    //         <a
    //           href="https://twitter.com"
    //           target="_blank"
    //           rel="noopener noreferrer"
    //         >
    //           <img
    //             src="/assets/icons/new-twitter.png"
    //             alt="Twitter"
    //             className="h-8 w-8 mx-8"
    //           />
    //         </a>
    //         <a
    //           href="https://instagram.com"
    //           target="_blank"
    //           rel="noopener noreferrer"
    //         >
    //           <img
    //             src="/assets/icons/instagram.png"
    //             alt="Instagram"
    //             className="h-8 w-8 mx-8"
    //           />
    //         </a>
    //       </div>
    //     </div>
    //   </div>
    // </div>,
  ];

  return (
    <div
      className="w-full h-screen -mt-20 flex flex-col relative overflow-hidden select-none"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp} // Handle mouse leaving the area
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onDragStart={(e) => e.preventDefault()}
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
    >
      {/* Sections Container */}
      <div
        className="flex transition-transform duration-700 ease-in-out h-full"
        style={{ transform: `translateX(-${currentSection * 100}%)` }}
      >
        {sections.map((section, index) => (
          <div key={index} className="w-full flex-shrink-0 h-full">
            {section}
          </div>
        ))}
      </div>

      {/* Section Indicators */}
      <div className="fixed top-1/2 left-4 transform -translate-y-1/2 z-50 flex flex-col space-y-2">
        {sections.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSection(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSection === index ? "bg-white" : "bg-white/30"
              }`}
          />
        ))}
      </div>

      {/* Bottom Navigation Panel */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-500 ease-in-out ${isPanelExpanded ? "translate-y-0" : "translate-y-16"
          }`}
      >
        <div className="bg-transparent backdrop-blur-md border-t border-white/20">
          {/* Toggle Button */}
          <div className="flex justify-center text-white">
            <button
              onClick={togglePanel}
              className="bg-gray-700 hover:bg-gray-600 p-3 rounded-full -mt-6 transition-all duration-300 shadow-lg"
            >
              {isPanelExpanded ? (
                <ChevronDown size={24} />
              ) : (
                <ChevronUp size={24} />
              )}
            </button>
          </div>

          {/* Navigation Items */}
          <div className="flex justify-between items-center">
            {/* Left: Apartment Type Buttons */}
            <div className="flex items-center px-8 py-2 space-x-4">
              {[
                {
                  label: "1-Bedroom Apartments",
                  icon: "/assets/icons/bed-single-02.png",
                  disabled: false,
                  onClick: () => navigate("/floorplan"),
                },
              ].map(({ label, icon, disabled, onClick }, index) => (
                <button
                  key={index}
                  onClick={onClick}
                  disabled={disabled}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full backdrop-blur-sm transition-all duration-300 text-white ${disabled
                    ? "cursor-not-allowed bg-gray-700/50 text-white/50"
                    : "bg-gray-700 hover:bg-gray-600 "
                    }`}
                >
                  <img src={icon} alt={label} className="w-4 h-4" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>

            {/* Right: Navigation Arrows & Sound */}
            <div className="flex items-center px-8 py-4 space-x-4">
              <button
                onClick={goToPrevSection}
                className="w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all duration-300 flex items-center justify-center "
              >
                <img
                  src="/assets/icons/left-arrow.png"
                  alt="Previous"
                  className="w-4 h-4"
                />
              </button>

              <SoundToggleButton />

              <button
                onClick={goToNextSection}
                className="w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all duration-300 flex items-center justify-center "
              >
                <img
                  src="/assets/icons/right-arrow.png"
                  alt="Next"
                  className="w-4 h-4"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fade-in-left {
          animation: fadeInLeft 1s ease-out forwards;
        }

        .animate-fade-in-right {
          animation: fadeInRight 1s ease-out forwards;
        }
      `}</style>

      {/* FAB - Enquire Now */}
      {currentSection !== 8 && (
        <button
          onClick={() => setCurrentSection(8)}
          className="fixed bottom-24 right-8 z-50 bg-gray-700 hover:bg-gray-600 text-white py-3 px-6 rounded-full shadow-lg transition-all duration-300"
        >
          Enquire Now
        </button>
      )}

      {dialogVisible && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className={`transform transition-all duration-300 ease-in-out
        ${showDialog ? "opacity-100 scale-100" : "opacity-0 scale-95"}
        bg-black/10 backdrop-blur-md border border-white/20 
        rounded-2xl p-8 shadow-xl text-center text-white max-w-sm w-full mx-4`}
          >
            <h2 className="text-xl md:text-2xl font-semibold mb-2 text-[#DCC5B7]">
              Thank You!
            </h2>
            <p className="text-sm md:text-base text-white">
              Your details have been submitted successfully.
            </p>
            <button
              onClick={() => {
                setShowDialog(false);
                setTimeout(() => setDialogVisible(false), 300);
              }}
              className="mt-4 px-4 py-2 rounded-full bg-gray-700/30 hover:bg-gray-600/50 text-white text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
